import {
  ImageStyle,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
  Image,
  Pressable,
} from 'react-native';
import React, {useLayoutEffect, useMemo, useState} from 'react';
import {Button, Text} from 'ui-kits';
import colors from 'theme/Colors';
import {corner_radius, spacing} from 'theme/Units';
import {getCallenderDay} from 'utils/getCallenderDays';
import {v4 as uuid} from 'uuid';
import {SimpleDropdown} from 'ui-kits/Dropdown/SimpleDropdown';
import {years, months} from 'constants/DefaultValues';

type propTypes = Readonly<{
  onClose: () => void;
  onChangeDate: (selectedDate: Date) => void;
  style?: ViewStyle;
  value: Date | null;
}>;

export function Calendar(props: propTypes) {
  const {onChangeDate, onClose, style, value} = props;
  const [yearDropDownShown, setYearDropDownShown] = useState<boolean>(false);
  const [monthDropDownShown, setMonthDropDownShown] = useState<boolean>(false);

  const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [previewDate, setPreviewDate] = useState<Date>(new Date());

  useLayoutEffect(() => {
    if (value) {
      setCurrentDate(value);
      setPreviewDate(value);
    }
  }, [value]);

  const days = useMemo(() => {
    return getCallenderDay(previewDate.getMonth(), previewDate.getFullYear());
  }, [previewDate]);

  const changeMonth = (direction: -1 | 1) => {
    setPreviewDate(prevDate => {
      let newMonth = prevDate.getMonth() + direction;
      return new Date(prevDate.getFullYear(), newMonth, 1);
    });
  };

  const isToday = (item: {day: number; month: number; year: number}) => {
    return (
      item.day === currentDate.getDate() &&
      item.month === currentDate.getMonth() &&
      item.year === currentDate.getFullYear()
    );
  };

  function handleYearSelect(year: string) {
    setYearDropDownShown(false);
    setPreviewDate(p => {
      const newYear = parseInt(year, 10);
      const daysInNewMonth = new Date(newYear, p.getMonth() + 1, 0).getDate();
      return new Date(
        newYear,
        p.getMonth(),
        Math.min(p.getDate(), daysInNewMonth),
      );
    });
  }

  function handleMonthSelect(year: string, index?: number) {
    setMonthDropDownShown(false);
    setPreviewDate(p => {
      const newMonth = index!;
      const daysInNewMonth = new Date(
        p.getFullYear(),
        newMonth + 1,
        0,
      ).getDate();
      return new Date(
        p.getFullYear(),
        newMonth,
        Math.min(p.getDate(), daysInNewMonth),
      );
    });
  }

  return (
    <View style={[styles.calendarContainer, style]} id="top-overlay">
      {/* header */}
      <View style={styles.header}>
        {/* go to previous month */}
        <TouchableOpacity
          onPress={() => changeMonth(-1)}
          style={styles.arrowButton}>
          <Image
            style={styles.arrowIcon}
            source={require('assets/icons/arrow_left@2x.png')}
          />
        </TouchableOpacity>

        {/* current month */}
        <View style={styles.headerLabel}>
          <Pressable
            onPress={() => {
              setMonthDropDownShown(p => !p);
              setYearDropDownShown(false);
            }}>
            <Text selectable={false} style={styles.monthHeading}>
              {previewDate.toLocaleString('default', {month: 'long'})}
            </Text>
          </Pressable>
          <Pressable
            onPress={() => {
              setYearDropDownShown(p => !p);
              setMonthDropDownShown(false);
            }}>
            <Text selectable={false} style={styles.monthHeading}>
              {previewDate.getFullYear()}
            </Text>
          </Pressable>
        </View>

        {/* go to next month */}
        <TouchableOpacity
          onPress={() => changeMonth(1)}
          style={styles.arrowButton}>
          <Image
            style={styles.arrowIcon}
            source={require('assets/icons/arrow_right@2x.png')}
          />
        </TouchableOpacity>
      </View>

      {/* Weekday Labels */}
      <View style={styles.weekdays}>
        {daysOfWeek.map(day => (
          <Text key={uuid()} style={styles.weekdayText}>
            {day}
          </Text>
        ))}
      </View>

      {/* calender days */}
      <View style={styles.daysContainer}>
        {/* previous month days */}
        {days.prev.map(item => (
          <Pressable
            onPress={() => changeMonth(-1)}
            key={uuid()}
            style={styles.dateTextContainer}>
            <Text style={[styles.dateText, styles.otherDays]} key={uuid()}>
              {item.day}
            </Text>
          </Pressable>
        ))}

        {/* current month days */}
        {days.current.map(item => (
          <Pressable
            onPress={() => {
              setCurrentDate(p => new Date(item.year, item.month, item.day));
            }}
            key={uuid()}
            style={[
              styles.dateTextContainer,
              isToday(item) ? styles.currentDayBox : null,
            ]}>
            <Text
              style={[
                styles.dateText,
                isToday(item) ? styles.currentDay : null,
              ]}>
              {item.day}
            </Text>
          </Pressable>
        ))}

        {/* next month days */}
        {days.next.map(item => (
          <Pressable
            onPress={() => changeMonth(1)}
            key={uuid()}
            style={styles.dateTextContainer}>
            <Text style={[styles.dateText, styles.otherDays]} key={uuid()}>
              {item.day}
            </Text>
          </Pressable>
        ))}
      </View>

      {/* action buttons */}
      <View style={styles.actionButtonWrapper}>
        <Button
          onPress={onClose}
          style={styles.actionButton}
          text="cancel"
          variant="secondary"
        />
        <Button
          onPress={() => {
            onChangeDate(currentDate);
            onClose();
          }}
          style={styles.actionButton}
          text="Apply"
          variant="primary"
        />
      </View>

      {yearDropDownShown && (
        <SimpleDropdown
          options={years}
          style={styles.yearDropDown}
          onSelect={handleYearSelect}
        />
      )}

      {monthDropDownShown && (
        <SimpleDropdown
          options={months}
          style={styles.monthDropDown}
          onSelect={handleMonthSelect}
        />
      )}
    </View>
  );
}

type styleTypes = {
  calendarContainer: ViewStyle;
  actionButton: ViewStyle;
  monthHeading: TextStyle;
  header: ViewStyle;
  arrowIcon: ImageStyle;
  arrowButton: ViewStyle;
  weekdays: ViewStyle;
  weekdayText: TextStyle;
  daysContainer: ViewStyle;
  dateText: TextStyle;
  dateTextContainer: ViewStyle;
  otherDays: TextStyle;
  currentDay: TextStyle;
  currentDayBox: ViewStyle;
  actionButtonWrapper: ViewStyle;
  yearDropDown: ViewStyle;
  monthDropDown: ViewStyle;
  headerLabel: ViewStyle;
};
const styles: styleTypes = {
  headerLabel: {
    flexDirection: 'row',
    gap: spacing['1'],
    alignItems: 'center',
  },
  actionButtonWrapper: {
    flexDirection: 'row',
    gap: spacing['4'],
    alignSelf: 'center',
  },
  calendarContainer: {
    borderWidth: 1,
    borderColor: colors.palette.stroke,
    padding: 10,
    backgroundColor: colors.palette.white,
    gap: spacing['2'],
    borderRadius: corner_radius['rounded-md'],
    minWidth: 250,
    zIndex: 999,
    position: 'absolute',
    boxShadow: '0px 0px 10px 2px rgba(0, 0, 0, 0.1)',
  },
  yearDropDown: {
    position: 'absolute',
    zIndex: 999,
    width: 64,
    right: 48,
    top: 40,
    maxHeight: 220,
    padding: spacing['1'],
    alignItems: 'center',
  },
  monthDropDown: {
    position: 'absolute',
    zIndex: 999,
    width: 64,
    left: 64,
    top: 40,
    maxHeight: 220,
    padding: spacing['1'],
    alignItems: 'center',
  },
  arrowButton: {
    borderColor: colors.palette.stroke,
    borderWidth: 2,
    borderStyle: 'solid',
    padding: spacing['2'],
    borderRadius: corner_radius['rounded-sm'],
  },
  arrowIcon: {
    width: 14,
    height: 14,
    resizeMode: 'contain',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  actionButton: {width: 100, height: 36},
  monthHeading: {
    fontSize: 18,
    fontWeight: 600,
  },
  weekdays: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  weekdayText: {
    flex: 1,
    textAlign: 'center',
    fontSize: 10,
    fontWeight: 500,
    color: colors.palette.secondaryBlack5,
  },
  daysContainer: {
    display: 'grid',
    gridTemplateRows: 'repeat(6, 1fr)', // 6 rows format
    gridTemplateColumns: 'repeat(7, 1fr)', // 7 week days
    width: '100%',
  },
  dateText: {
    textAlign: 'center',
    fontSize: 12,
    fontWeight: '500',
  },
  dateTextContainer: {
    aspectRatio: '1/1',
    justifyContent: 'center',
    alignItems: 'center',
  },
  otherDays: {
    color: colors.palette.nuetral4,
    fontWeight: '100',
  },
  currentDay: {
    color: colors.palette.brandBlue,
    fontWeight: 'bold',
  },
  currentDayBox: {
    borderColor: colors.palette.nuetral4,
    borderWidth: 1,
    borderRadius: corner_radius['rounded-sm'],
  },
};

